import { type CreateTRPCClientOptions, createTRPCClient, httpLink, TRPCClientError, type TRPCLink } from '@trpc/client'
import { observable } from '@trpc/server/observable'
import { createTRPCContext, createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { DateTime } from 'luxon'
import superjson from 'superjson'

import type { AppRouter, Cradle, ORUS_TRPC_ERROR_CODE } from '@orus.eu/backend'
import { TechnicalError } from '@orus.eu/error'
import { enqueueTemporaryNotificationAlert } from '@orus.eu/pharaoh'
import { translate } from '@orus.eu/translations'

import { currentLanguageForNotificationsRef } from './lib/current-language-for-notifications.ts'
import { isProductionEnvironment } from './lib/env-utils.ts'
import { logger } from './lib/logger.ts'
import { queryClient } from './shared/query-client.ts'
import { buildLoginHref } from './shared/routing/build-login-href.ts'
import { recordLoginBounce } from './shared/routing/detect-login-redirect-loop.ts'

type ResponseWithHeaders = {
  headers: {
    get: (name: string) => string | null
  }
}

const errorOrSuggestUpdateLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next: (value) => {
          const response = value.context?.['response'] as ResponseWithHeaders

          if (response && response.headers && response.headers.get('update-webapp') === 'Suggested') {
            window.dispatchEvent(new Event('update-webapp-suggested'))
          }

          observer.next(value)
        },
        error: (err) => {
          observer.error(err)
          const { data } = err

          // If there's no data, it's a network error, and we don't want to do anything other than notify the user
          if (!data) {
            // Aborted or navigated-away fetches (e.g. OAuth redirect) often surface on Safari as "Load failed"
            // with no TRPC payload; avoid a misleading connection-error toast.
            if (!isLoadFailedMessageTrpcClientError(err)) {
              notifyNetworkError()
            }
            return
          }

          const { httpStatus, code, path } = data

          if (!isProductionEnvironment()) {
            logger.info(`Received tRPC ${code}${path ? ` on ${path}` : ''}`)
          }

          // The code below does something similar to what happens in ErrorBoundary when a TRCP error is thrown
          // by a direct trpc call.
          // We can live with this duplication, because directly using trpc calls is deprecated and should be removed in the
          // long run.
          // A code this client does not know yet, sent by a newer backend
          for (const action of trcpCodeFollowupActions[code] ?? ['report-sentry', 'technical-issue-notification']) {
            switch (action) {
              case 'report-sentry':
                logger.error(
                  new TechnicalError(`Received tRPC ${code}${path ? ` on ${path}` : ''}`, {
                    context: {
                      trpcPath: path,
                      trpcCode: code,
                      httpStatus,
                      errMessage: err.message,
                      clientAppVersion: __CODE_VERSION_ID__,
                    },
                    cause: err,
                  }),
                )
                break
              case 'redirect-to-login':
                redirectToLogin()
                break
              case 'redirect-to-not-found':
                redirectToNotFound()
                break
              case 'technical-issue-notification':
                notifyTechnicalIssue(code)
                break
            }
          }
        },
        complete: () => {
          observer.complete()
        },
      })

      return unsubscribe
    })
  }
}

export function redirectToLogin(): void {
  const loginHref = buildLoginHref()
  recordLoginBounce(loginHref)
  window.location.href = new URL(loginHref, window.location.origin).toString()
}

function redirectToNotFound() {
  const notFoundUrl = new URL('/not-found', window.location.origin)
  history.replaceState({}, '', notFoundUrl.toString())
}

function getErrorMessageByErrorCode(code: ORUS_TRPC_ERROR_CODE): string {
  switch (code) {
    case 'TIMEOUT':
      return translate('error_client_timeout', currentLanguageForNotificationsRef.current)
    case 'SERVER_OUTDATED':
      return translate('error_client_server_outdated', currentLanguageForNotificationsRef.current)
    case 'SERVICE_UNAVAILABLE':
      return translate('error_client_service_unavailable', currentLanguageForNotificationsRef.current)
    case 'TOO_MANY_REQUESTS':
      return translate('error_client_too_many_requests', currentLanguageForNotificationsRef.current)
    default:
      return translate('error_client_technical_issue', currentLanguageForNotificationsRef.current)
  }
}

let technicalIssueLastNotifiedTimestamp: number | null = null
function notifyTechnicalIssue(code: ORUS_TRPC_ERROR_CODE) {
  // eslint-disable-next-line no-restricted-syntax -- client.ts is the deliberate seam for the move-time-forward dev feature, see eslint.config.js
  const now = DateTime.now()
  const oneMinuteAgo = now.minus({ minutes: 1 }).toMillis()

  if (!technicalIssueLastNotifiedTimestamp || technicalIssueLastNotifiedTimestamp < oneMinuteAgo) {
    const message = getErrorMessageByErrorCode(code)
    enqueueTemporaryNotificationAlert(message, {
      variant: 'danger',
    })
    technicalIssueLastNotifiedTimestamp = now.toMillis()
  }
}

function isLoadFailedMessageTrpcClientError(err: unknown): boolean {
  return err instanceof TRPCClientError && err.message === 'Load failed'
}

let networkErrorLastNotifiedTimestamp: number | null = null
function notifyNetworkError() {
  // eslint-disable-next-line no-restricted-syntax -- client.ts is the deliberate seam for the move-time-forward dev feature, see eslint.config.js
  const now = DateTime.now()
  const oneMinuteAgo = now.minus({ minutes: 1 }).toMillis()

  if (!networkErrorLastNotifiedTimestamp || networkErrorLastNotifiedTimestamp < oneMinuteAgo) {
    networkErrorLastNotifiedTimestamp = now.toMillis()
    const language = currentLanguageForNotificationsRef.current
    enqueueTemporaryNotificationAlert(translate('connection_error', language), {
      variant: 'danger',
    })
  }
}

const clientConfig: CreateTRPCClientOptions<AppRouter> = {
  links: [
    errorOrSuggestUpdateLink,
    httpLink<AppRouter>({
      url: '/trpc',
      transformer: superjson,
      headers: {
        'client-version': __CODE_VERSION_ID__,
      },
    }),
  ],
}

/**
 * @deprecated Use {@link useTRPC} instead.
 */
export const trpcClient_DO_NOT_USE_ONLY_FOR_TANSTACK_QUERY_PROVIDER = createTRPCClient<AppRouter>(clientConfig)

const trpcContext = createTRPCContext<AppRouter>()
export const { useTRPC, useTRPCClient, TRPCProvider } = trpcContext

/**
 * tRPC proxy usable OUTSIDE React (route loaders / beforeLoad). Produces `.queryOptions()` to pass to
 * `queryClient.ensureQueryData(...)`. Shares the same client + queryClient as the React provider so the
 * cache is unified. Pattern: createTRPCOptionsProxy from @trpc/tanstack-react-query.
 */
export const trpcLoader = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient_DO_NOT_USE_ONLY_FOR_TANSTACK_QUERY_PROVIDER,
  queryClient,
})

type TrcpErrorFollowupAction =
  | 'report-sentry'
  | 'redirect-to-login'
  | 'redirect-to-not-found'
  | 'technical-issue-notification'

const trcpCodeFollowupActions: Record<ORUS_TRPC_ERROR_CODE, TrcpErrorFollowupAction[]> = {
  PARSE_ERROR: ['report-sentry', 'technical-issue-notification'],
  BAD_REQUEST: ['report-sentry', 'technical-issue-notification'],
  INTERNAL_SERVER_ERROR: ['technical-issue-notification'],
  NOT_IMPLEMENTED: ['report-sentry', 'technical-issue-notification'],
  UNAUTHORIZED: ['redirect-to-login'],
  FORBIDDEN: [], // user is authenticated but doesn't have enough rights, so we just show the error message
  NOT_FOUND: ['redirect-to-not-found'],
  METHOD_NOT_SUPPORTED: ['report-sentry', 'technical-issue-notification'],
  TIMEOUT: ['technical-issue-notification'],
  CONFLICT: ['report-sentry', 'technical-issue-notification'],
  PRECONDITION_FAILED: ['report-sentry', 'technical-issue-notification'],
  PRECONDITION_REQUIRED: ['report-sentry', 'technical-issue-notification'],
  PAYLOAD_TOO_LARGE: ['report-sentry', 'technical-issue-notification'],
  UNPROCESSABLE_CONTENT: ['report-sentry', 'technical-issue-notification'],
  TOO_MANY_REQUESTS: ['technical-issue-notification'],
  CLIENT_CLOSED_REQUEST: ['report-sentry', 'technical-issue-notification'],
  BAD_GATEWAY: ['report-sentry', 'technical-issue-notification'],
  SERVICE_UNAVAILABLE: ['report-sentry', 'technical-issue-notification'],
  GATEWAY_TIMEOUT: ['report-sentry', 'technical-issue-notification'],
  UNSUPPORTED_MEDIA_TYPE: ['report-sentry', 'technical-issue-notification'],
  PAYMENT_REQUIRED: ['report-sentry', 'technical-issue-notification'],
  CLIENT_OUTDATED: [], // handled separately by the SessionManager
  SERVER_OUTDATED: ['technical-issue-notification'],
}

/**
 * Since we put Dependencies in the global scope, VS Code has trouble inferring the types
 * of the router methods.
 * Making a direct import of the Cradle type helps workaround this problem.
 */
const vsCodeTrpcRouterTypeInferrenceWorkaround: Cradle | undefined = undefined
void vsCodeTrpcRouterTypeInferrenceWorkaround
